/**
 * offlineDb.ts — IndexedDB wrapper for offline data caching.
 *
 * Uses the `idb` library for a promise-based IndexedDB API.
 * Stores patients, appointments, medications, and services for
 * offline read access.
 */

import { openDB, type IDBPDatabase } from 'idb'

const DB_NAME = 'mashfa-offline'
const DB_VERSION = 1

interface PraxiDB {
  patients: {
    key: number
    value: Record<string, unknown>
    indexes: { 'by-name': string }
  }
  appointments: {
    key: number
    value: Record<string, unknown>
    indexes: { 'by-date': string }
  }
  medications: {
    key: number
    value: Record<string, unknown>
    indexes: { 'by-name': string }
  }
  services: {
    key: number
    value: Record<string, unknown>
  }
  syncQueue: {
    key: number
    value: {
      url: string
      method: string
      body: string | null
      timestamp: number
      retries: number
    }
  }
}

let dbPromise: Promise<IDBPDatabase<PraxiDB>> | null = null

/**
 * Get or create IndexedDB connection.
 * If opening fails, resets and retries once.
 */
export function getDb(): Promise<IDBPDatabase<PraxiDB>> {
  if (!dbPromise) {
    dbPromise = openDB<PraxiDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Patients
        if (!db.objectStoreNames.contains('patients')) {
          const store = db.createObjectStore('patients', { keyPath: 'id' })
          store.createIndex('by-name', 'last_name')
        }
        // Appointments
        if (!db.objectStoreNames.contains('appointments')) {
          const store = db.createObjectStore('appointments', { keyPath: 'id' })
          store.createIndex('by-date', 'start_time')
        }
        // Medications
        if (!db.objectStoreNames.contains('medications')) {
          const store = db.createObjectStore('medications', { keyPath: 'id' })
          store.createIndex('by-name', 'name')
        }
        // Services
        if (!db.objectStoreNames.contains('services')) {
          db.createObjectStore('services', { keyPath: 'id' })
        }
        // Sync queue
        if (!db.objectStoreNames.contains('syncQueue')) {
          db.createObjectStore('syncQueue', { autoIncrement: true })
        }
      },
      blocked() {
        console.warn('IndexedDB blocked - another tab may have older version')
      },
      blocking() {
        console.warn('IndexedDB blocking - closing to allow upgrade')
      },
      terminated() {
        console.error('IndexedDB connection terminated unexpectedly')
        dbPromise = null // Reset so next call retries
      },
    }).catch((error) => {
      console.error('IndexedDB failed to open:', error)
      dbPromise = null // Reset promise to allow retry
      // Try to delete corrupted DB
      if (typeof indexedDB !== 'undefined') {
        indexedDB.deleteDatabase(DB_NAME)
      }
      throw error
    })
  }
  return dbPromise
}

// ── Cache writers (called after successful API GET) ──────────
// All cache operations are wrapped with timeout and error handling
// to prevent blocking the app if IndexedDB fails

export async function withTimeout<T>(promise: Promise<T>, timeoutMs = 10000): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('IndexedDB operation timeout')), timeoutMs)
    ),
  ])
}

export async function cachePatients(patients: Record<string, unknown>[]): Promise<void> {
  try {
    const db = await withTimeout(getDb())
    const tx = db.transaction('patients', 'readwrite')
    await withTimeout(Promise.all([
      ...patients.map((p) => tx.store.put(p)),
      tx.done,
    ]))
  } catch (error) {
    console.warn('Failed to cache patients:', error)
    // Silently fail - caching is optional
  }
}

export async function cacheAppointments(appointments: Record<string, unknown>[]): Promise<void> {
  try {
    const db = await withTimeout(getDb())
    const tx = db.transaction('appointments', 'readwrite')
    await withTimeout(Promise.all([
      ...appointments.map((a) => tx.store.put(a)),
      tx.done,
    ]))
  } catch (error) {
    console.warn('Failed to cache appointments:', error)
  }
}

export async function cacheMedications(medications: Record<string, unknown>[]): Promise<void> {
  try {
    const db = await withTimeout(getDb())
    const tx = db.transaction('medications', 'readwrite')
    await withTimeout(Promise.all([
      ...medications.map((m) => tx.store.put(m)),
      tx.done,
    ]))
  } catch (error) {
    console.warn('Failed to cache medications:', error)
  }
}

export async function cacheServices(services: Record<string, unknown>[]): Promise<void> {
  try {
    const db = await withTimeout(getDb())
    const tx = db.transaction('services', 'readwrite')
    await withTimeout(Promise.all([
      ...services.map((s) => tx.store.put(s)),
      tx.done,
    ]))
  } catch (error) {
    console.warn('Failed to cache services:', error)
  }
}

// ── Cache readers (used when offline) ────────────────────────

export async function getOfflinePatients(): Promise<Record<string, unknown>[]> {
  try {
    const db = await withTimeout(getDb())
    return await withTimeout(db.getAll('patients'))
  } catch (error) {
    console.warn('Failed to read offline patients:', error)
    return []
  }
}

export async function getOfflineAppointments(): Promise<Record<string, unknown>[]> {
  try {
    const db = await withTimeout(getDb())
    return await withTimeout(db.getAll('appointments'))
  } catch (error) {
    console.warn('Failed to read offline appointments:', error)
    return []
  }
}

export async function getOfflineMedications(): Promise<Record<string, unknown>[]> {
  try {
    const db = await withTimeout(getDb())
    return await withTimeout(db.getAll('medications'))
  } catch (error) {
    console.warn('Failed to read offline medications:', error)
    return []
  }
}

export async function getOfflineServices(): Promise<Record<string, unknown>[]> {
  try {
    const db = await withTimeout(getDb())
    return await withTimeout(db.getAll('services'))
  } catch (error) {
    console.warn('Failed to read offline services:', error)
    return []
  }
}

// ── Clear cache (for troubleshooting) ────────────────────────

/**
 * Delete the entire IndexedDB database.
 * Useful for troubleshooting cache corruption or timeout issues.
 * Call this from browser console: clearAllOfflineData()
 */
export async function clearAllOfflineData(): Promise<void> {
  try {
    // Close current connection if open
    if (dbPromise) {
      const db = await dbPromise
      db.close()
      dbPromise = null
    }
    
    // Delete the database
    if (typeof indexedDB !== 'undefined') {
      await new Promise<void>((resolve, reject) => {
        const request = indexedDB.deleteDatabase(DB_NAME)
        request.onsuccess = () => {
          console.log('✅ IndexedDB cache cleared successfully')
          resolve()
        }
        request.onerror = () => {
          console.error('❌ Failed to clear IndexedDB:', request.error)
          reject(request.error)
        }
        request.onblocked = () => {
          console.warn('⚠️ IndexedDB deletion blocked - close all tabs and try again')
        }
      })
    }
  } catch (error) {
    console.error('Failed to clear offline data:', error)
    throw error
  }
}

// Make it available globally for console access
if (typeof window !== 'undefined') {
  (window as any).clearAllOfflineData = clearAllOfflineData
}
